import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Router } from '@angular/router';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  resetRegistration,
  selectRegistrationErrorType,
  selectRegistrationIsRegisteringInProgress,
  selectRegistrationQrCodeDataUri
} from '@qvs/core/registration';
import { RegistrationErrorComponent } from '@ebanking/features/login/registration-error/registration-error.component';
import { loginRoutePaths } from '@ebanking/features/login/login.route-paths';
import { TranslatePipe } from '@ngx-translate/core';
import { FalsyPipe } from '~/shared/pipes/falsy.pipe';
import { SvgIconComponent } from '~/shared/form-elements/components/svg-icon/svg-icon.component';
import { selectContractNumber } from '@qvs/core/auth';
import { LoginPageHeaderComponent } from '@ebanking/features/login/login-page-header/login-page-header.component';

type RegistrationErrorType = 'general' | 'expired';

interface RegistrationErrorContent {
  title: string;
  paragraphOne: string;
  paragraphTwo: string;
  supportMail: string;
  supportPhone: string;
  buttonText: string;
}

@Component({
  selector: 'qvs-registration-qr',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    RegistrationErrorComponent,
    TranslatePipe,
    FalsyPipe,
    SvgIconComponent,
    LoginPageHeaderComponent
  ],
  templateUrl: './registration-qr.component.html',
  styleUrls: ['./registration-qr.component.scss']
})
export class RegistrationQrComponent implements OnDestroy {
  dataUri$: Observable<string | null>;
  isRegistrationInProgress$: Observable<boolean>;
  errorContent$: Observable<RegistrationErrorContent | null>;
  description$: Observable<string>;
  descriptionIcon$: Observable<string>;
  contractNumber$: Observable<string | null>;

  private readonly destroyed$ = new Subject<void>();

  private readonly registrationErrorContent: Record<RegistrationErrorType, RegistrationErrorContent> = {
    general: {
      title: 'ebanking.registration.GENERAL_ERROR_TITLE',
      paragraphOne: 'ebanking.registration.GENERAL_ERROR_TEXT',
      paragraphTwo: 'ebanking.registration.GENERAL_ERROR_SUBTEXT',
      supportMail: 'ebanking.registration.GENERAL_ERROR_SUPPORT_MAIL',
      supportPhone: 'ebanking.registration.GENERAL_ERROR_SUPPORT_NUMBER',
      buttonText: 'ebanking.registration.GENERAL_ERROR_BUTTON'
    },
    expired: {
      title: 'ebanking.registration.ERROR_EXPIRED_TITLE',
      paragraphOne: 'ebanking.registration.ERROR_EXPIRED_TEXT',
      paragraphTwo: 'ebanking.registration.GENERAL_ERROR_SUBTEXT',
      supportMail: 'ebanking.registration.GENERAL_ERROR_SUPPORT_MAIL',
      supportPhone: 'ebanking.registration.GENERAL_ERROR_SUPPORT_NUMBER',
      buttonText: 'ebanking.registration.GENERAL_ERROR_BUTTON'
    }
  };
  constructor(
    private readonly router: Router,
    private readonly store: Store
  ) {
    this.dataUri$ = this.store.pipe(
      select(selectRegistrationQrCodeDataUri),
      filter((qr) => !!qr),
      takeUntil(this.destroyed$)
    );
    this.isRegistrationInProgress$ = this.store.pipe(
      select(selectRegistrationIsRegisteringInProgress),
      takeUntil(this.destroyed$)
    );
    this.errorContent$ = this.store.pipe(
      select(selectRegistrationErrorType),
      takeUntil(this.destroyed$),
      map((errorType) => (errorType ? this.registrationErrorContent[errorType] : null))
    );
    this.description$ = this.isRegistrationInProgress$.pipe(
      takeUntil(this.destroyed$),
      map((isEnrollingInProgress) =>
        isEnrollingInProgress ? 'ebanking.registration.SCAN_INSTRUCTION' : 'ebanking.registration.SCAN_SUCCESS'
      )
    );
    this.descriptionIcon$ = this.isRegistrationInProgress$.pipe(
      takeUntil(this.destroyed$),
      map((isEnrollingInProgress) => (isEnrollingInProgress ? 'assets/icons/scan-qr.svg' : 'assets/images/success.svg'))
    );
    this.contractNumber$ = this.store.pipe(
      select(selectContractNumber),
      filter((result) => !!result),
      takeUntil(this.destroyed$)
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  startNewRegistration() {
    this.store.dispatch(resetRegistration());
    this.router.navigate([loginRoutePaths.REGISTRATION]);
  }
}
